import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { toast } from "react-toastify";
import apiClient from "../utils/axiosConfig";
import { 
  BsEnvelope, 
  BsCheckCircle, 
  BsXCircle,
  BsArrowLeft
} from "react-icons/bs";

const Unsubscribe = () => {
  const [email, setEmail] = useState("");
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsUnsubscribing(true);
    setError("");

    try {
      // In a real implementation, you would call your backend API to unsubscribe
      // For now, we'll simulate the unsubscribe process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate successful unsubscribe
      setUnsubscribed(true);
      toast.success("You have been successfully unsubscribed from our newsletter");
    } catch (err) {
      setError("Failed to unsubscribe. Please try again later.");
      toast.error("Failed to unsubscribe. Please try again later.");
    } finally {
      setIsUnsubscribing(false);
    }
  };

  const handleResubscribe = () => {
    navigate("/newsletter");
  };

  return (
    <>
      <Meta 
        title="Unsubscribe from VEDA Newsletter"
        description="Unsubscribe from VEDA's newsletter to stop receiving updates on mythological toys, games, and puzzles."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-4 shadow-lg">
              <BsEnvelope className="text-white text-3xl" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">VEDA Newsletter</h1>
            <p className="text-gray-600">Manage your subscription preferences</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            {!unsubscribed ? (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Unsubscribe from Newsletter</h2>
                  <p className="text-gray-600">
                    Enter your email address below to unsubscribe from our newsletter. You will no longer receive updates on new products, special offers, and mythological stories.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <BsXCircle className="h-5 w-5 text-red-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleUnsubscribe} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <BsEnvelope className="text-red-500 text-lg" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-300"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isUnsubscribing}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isUnsubscribing ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>Unsubscribe</span>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <BsCheckCircle className="text-green-500 text-4xl" />
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Successfully Unsubscribed</h2>
                <p className="text-gray-600 mb-8">
                  You have been successfully unsubscribed from our newsletter. You will no longer receive updates from VEDA.
                </p>
                
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 mb-8">
                  <h3 className="font-bold text-yellow-800 mb-2">Changed Your Mind?</h3>
                  <p className="text-yellow-700 text-sm">
                    You can always resubscribe to our newsletter by visiting our website and signing up again.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleResubscribe}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105"
                  >
                    Resubscribe
                  </button>
                  
                  <button
                    onClick={() => navigate("/")}
                    className="px-6 py-3 bg-gray-100 text-gray-700 font-bold rounded-full shadow-lg hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-all flex items-center justify-center gap-2"
                  >
                    <BsArrowLeft /> Back to Home
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              Questions? Contact us at{" "}
              <a href="mailto:support@vedamerchandise.com" className="text-red-500 hover:text-red-600 font-medium">
                support@vedamerchandise.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Unsubscribe;